/**
 * Deep Linking — handles incoming URLs when the app is opened via
 * Universal Links (iOS) or App Links (Android).
 *
 * Call `initDeepLinking(router)` once from a root layout or client
 * component to start listening for URL-open events.
 */

import { isNativePlatform } from '@/lib/utils/platform'
import { isEntryRoute } from '@/lib/navigation/routes'

type RouterLike = {
  push: (url: string) => void
}

/**
 * The in-app href to open for an incoming Universal Link / App Link URL, or
 * null when its path is not an entry route. The allowlist is the entry-route
 * registry of lib/navigation/routes.ts (F8), which lists the routes of both
 * interfaces: an old link (`/chapters/…`, `?tab=`) redirects once it lands.
 * Query and `#anchor` are kept.
 */
export function deepLinkTarget(url: string): string | null {
  let parsed: URL
  try {
    // A bare path resolves against a dummy origin; only path, query and hash are used.
    parsed = new URL(url, 'https://app.invalid')
  } catch {
    return null
  }
  if (!url.startsWith('/') && !/^https?:\/\//i.test(url)) return null
  if (!isEntryRoute(parsed.pathname)) return null
  return parsed.pathname + parsed.search + parsed.hash
}

/**
 * Initialize deep link listener. Call once from a root client component.
 *
 * @returns Cleanup function to remove listener
 */
export async function initDeepLinking(router: RouterLike): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')

    // Handle URLs that open the app while it's running
    const listener = await App.addListener('appUrlOpen', ({ url }) => {
      const target = deepLinkTarget(url)
      if (target) router.push(target)
    })

    return () => {
      listener.remove()
    }
  } catch {
    // Deep linking is non-critical — degrade gracefully
    return () => {}
  }
}

/**
 * Handle the app being resumed from background.
 * Useful for refreshing stale data.
 *
 * @returns Cleanup function to remove listener
 */
export async function onAppResume(callback: () => void): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')
    const listener = await App.addListener('resume', callback)
    return () => {
      listener.remove()
    }
  } catch {
    return () => {}
  }
}

/**
 * Handle back button press (Android only).
 * Return `true` to prevent default behavior.
 *
 * @returns Cleanup function to remove listener
 */
export async function onBackButton(callback: (canGoBack: boolean) => void): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')
    const listener = await App.addListener('backButton', ({ canGoBack }) => {
      callback(canGoBack)
    })
    return () => {
      listener.remove()
    }
  } catch {
    return () => {}
  }
}
